import React, { useState, useEffect } from 'react';
import './index.css';
import DailyActivitySummary from './components/DailyActivitySummary';
import RefiHistory from './components/RefiHistory';
import CSVManagement from './components/CSVManagement';
import FeatureFlags from './components/FeatureFlags';
import ManualTrigger from './components/ManualTrigger';
import {  getFeatureFlags, getRefiHistory } from './services/api';

const PORTAL_URL = 'https://xzudlhkhzj.execute-api.us-east-2.amazonaws.com/prod/portal';
const API_BASE_URL = 'https://csv8ddssyf.execute-api.us-east-2.amazonaws.com';
const APP_VERSION = '2026.03.06';

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [featureFlags, setFeatureFlags] = useState(null);
  const [stats, setStats] = useState({ processedCount: 0, totalPayoffs: 0, totalFunding: 0, totalAdjustments: 0, netNewMoney: 0 });
  const [loading, setLoading] = useState(true);
  const [authenticating, setAuthenticating] = useState(true);
  const [user, setUser] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    validateSession();
  }, []);

  const validateSession = async () => {
    try {
      // Extract session from URL
      const urlParams = new URLSearchParams(window.location.search);
      const qls_session = urlParams.get('qls_session');

      console.log('🔍 Current URL:', window.location.href);
      console.log('🔍 URL Search Params:', window.location.search);
      console.log('🔍 Extracted qls_session:', qls_session);

      if (!qls_session) {
        console.warn('⚠️ No session ID found in URL, redirecting to portal...');
        alert('No authentication session found. Please access this dashboard through the QLS Portal.');
        window.location.href = PORTAL_URL;
        return;
      }

      // Store session
      setSessionId(qls_session);
      sessionStorage.setItem('qls_session', qls_session);
      console.log('✅ Session stored in sessionStorage:', sessionStorage.getItem('qls_session'));

      // Apply theme from URL param (primary) or localStorage fallback
      const theme = urlParams.get('theme') || localStorage.getItem('qls_theme') || 'light';
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('qls_theme', theme);
      console.log('🎨 Theme applied:', theme);

      // Clean SSO params from URL so they don't sit in browser history
      window.history.replaceState({}, document.title, window.location.pathname);

      // Validate session with backend
      console.log('🔐 Validating session with backend...');
      const response = await fetch(`${API_BASE_URL}/validate?qls_session=${qls_session}`);
      
      console.log('📡 Validation response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Validation failed:', errorText);
        throw new Error(`Session validation failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('📊 Validation response data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Invalid session');
      }

      // Store user info
      const userInfo = {
        email: data.user_email,
        name: data.user_name,
        role: data.user_role
      };
      
      setUser(userInfo);
      sessionStorage.setItem('user_email', userInfo.email);
      sessionStorage.setItem('user_name', userInfo.name);
      sessionStorage.setItem('user_role', userInfo.role);

      console.log('✅ Session validated successfully:', userInfo);
      console.log('✅ SessionStorage now contains:', {
        qls_session: sessionStorage.getItem('qls_session'),
        user_email: sessionStorage.getItem('user_email'),
        user_name: sessionStorage.getItem('user_name')
      });

      // Load dashboard data
      console.log('📊 Loading dashboard data...');
      await loadData();
      
    } catch (error) {
      console.error('❌ Session validation error:', error);
      alert('Your session has expired or is invalid. Redirecting to portal...');
      window.location.href = PORTAL_URL;
    } finally {
      setAuthenticating(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load feature flags
      const flagsResponse = await getFeatureFlags();
      setFeatureFlags(flagsResponse.data);
      
      // Load ALL refi history for aggregate financial stats
      const historyResponse = await getRefiHistory({ days: 365 * 10 }); // All time
      const history = historyResponse.data || [];
      
      // Calculate aggregate financial metrics (all-time)
      let totalPayoffs = 0;
      let totalFunding = 0;
      let totalAdjustments = 0;
      let processedCount = 0;
      
      history.forEach(refi => {
        // Only count successfully processed refis (not failed ones)
        if (refi.status === 'pending_approval' || refi.status === 'executed') {
          processedCount++;
          
          // Sum payoffs (will display as negative)
          const payoff = parseFloat(refi.refi_payoff) || 0;
          totalPayoffs += payoff;
          
          // Sum new loan funding
          const newLoanAmount = parseFloat(refi.new_loan_amount) || 0;
          totalFunding += newLoanAmount;
          
          // Overpayments are stored positive but represent credits (reduce principal) = display negative
          // Underpayments are stored negative but represent charges (increase borrowing) = display positive
          const adjustment = parseFloat(refi.adjustment_amount) || 0;
          totalAdjustments -= adjustment;
        }
      });
      
      // Calculate net new money: -payoffs + funding + adjustments
      const netNewMoney = -totalPayoffs + totalFunding + totalAdjustments;
      
      setStats({
        processedCount: processedCount,
        totalPayoffs: -totalPayoffs, // Display as negative
        totalFunding: totalFunding,
        totalAdjustments: totalAdjustments,
        netNewMoney: netNewMoney
      });
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToPortal = () => {
    // Pass session back to portal so it can restore the session
    const sessionId = sessionStorage.getItem('qls_session');
    if (sessionId) {
      window.location.href = `${PORTAL_URL}?qls_session=${sessionId}`;
    } else {
      window.location.href = PORTAL_URL;
    }
  };

  // Show loading during authentication
  if (authenticating) {
    return (
      <div className="App">
        <div className="card" style={{ margin: '50px auto', maxWidth: '400px', textAlign: 'center' }}>
          <div className="loading">
            <div className="spinner"></div>
            <p style={{ marginTop: '20px', color: 'var(--text-muted)' }}>Authenticating with QLS Portal...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show login prompt if no session
  if (!user) {
    return (
      <div className="App">
        <div className="card" style={{ margin: '50px auto', maxWidth: '400px', textAlign: 'center', padding: '40px' }}>
          <h2 style={{ marginBottom: '20px' }}>🔐 Authentication Required</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '30px' }}>
            Please log in through the QLS Portal to access the Refi Dashboard. (v{APP_VERSION})
          </p>
          <button
            className="button button-primary"
            onClick={handleReturnToPortal}
            style={{ width: '100%', padding: '15px' }}
          >
            Login with QLS Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Header */}
      <header style={styles.header}>
        <div className="container" style={styles.headerContent}>
          <div>
            <h1 style={styles.title}>Refi Accounting Dashboard</h1>
          </div>
          <div style={styles.userInfo}>
            <div style={{ textAlign: 'right', marginRight: '15px' }}>
              <div style={styles.userName}>{user.name}</div>
              <div style={styles.userEmail}>{user.email}</div>
            </div>
            <button
              className="button"
              onClick={handleReturnToPortal}
              style={styles.portalButton}
            >
              ← Return to Portal
            </button>
          </div>
        </div>
      </header>

      <div className="container">
        {/* Stats Cards - Aggregate Financial Metrics */}
        <div style={styles.statsContainer}>
          <div className="card" style={styles.statCard}>
            <div style={styles.statLabel}>Total Processed</div>
            <div style={{ ...styles.statValue, color: 'var(--accent-blue)' }}>{stats.processedCount || 0}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>All Time</div>
          </div>
          <div className="card" style={styles.statCard}>
            <div style={styles.statLabel}>Total Payoffs</div>
            <div style={{ ...styles.statValue, color: '#dc3545' }}>
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(stats.totalPayoffs || 0)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>Asset Value Lost</div>
          </div>
          <div className="card" style={styles.statCard}>
            <div style={styles.statLabel}>Total Funding</div>
            <div style={{ ...styles.statValue, color: '#28a745' }}>
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(stats.totalFunding || 0)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>New Money</div>
          </div>
          <div className="card" style={styles.statCard}>
            <div style={styles.statLabel}>Total Adjustments</div>
            <div style={{ ...styles.statValue, color: stats.totalAdjustments >= 0 ? '#28a745' : '#dc3545' }}>
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(stats.totalAdjustments || 0)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>Accounting</div>
          </div>
          <div className="card" style={styles.statCard}>
            <div style={styles.statLabel}>Total Net New Money</div>
            <div style={{ ...styles.statValue, color: stats.netNewMoney >= 0 ? 'var(--accent-blue)' : '#dc3545', fontWeight: 'bold' }}>
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(stats.netNewMoney || 0)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>Book Growth</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="card">
          <div style={styles.tabContainer}>
            <button
              className={`button ${activeTab === 'dashboard' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setActiveTab('dashboard')}
              style={styles.tabButton}
            >
              📊 Daily Activity
            </button>
            <button
              className={`button ${activeTab === 'history' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setActiveTab('history')}
              style={styles.tabButton}
            >
              📋 Refi History
            </button>
            <button
              className={`button ${activeTab === 'trigger' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setActiveTab('trigger')}
              style={styles.tabButton}
            >
              🔧 Manual Trigger
            </button>
            <button
              className={`button ${activeTab === 'csvs' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setActiveTab('csvs')}
              style={styles.tabButton}
            >
              📁 CSV Management
            </button>
            <button
              className={`button ${activeTab === 'flags' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setActiveTab('flags')}
              style={styles.tabButton}
            >
              🎛️ Feature Flags
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="card">
            <div className="loading">
              <div className="spinner"></div>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && <DailyActivitySummary onUpdate={loadData} />}
            {activeTab === 'history' && <RefiHistory />}
            {activeTab === 'trigger' && <ManualTrigger onTrigger={loadData} />}
            {activeTab === 'csvs' && <CSVManagement />}
            {activeTab === 'flags' && <FeatureFlags flags={featureFlags} onUpdate={loadData} />}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  header: {
    background: 'linear-gradient(135deg, #495057 0%, #343a40 100%)',
    color: 'white',
    padding: '20px 0',
    marginBottom: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  userName: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '2px',
  },
  userEmail: {
    fontSize: '13px',
    opacity: 0.8,
  },
  portalButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    borderRadius: '5px',
    transition: 'all 0.3s ease',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },
  statCard: {
    textAlign: 'center',
  },
  statLabel: {
    fontSize: '14px',
    color: 'var(--text-muted)',
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  tabContainer: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  tabButton: {
    minWidth: '150px',
  },
};

export default App;

